import * as form from './form';
import * as dialog from './dialog';
import {i18n} from './applicant/i18n';
import {Extendom} from 'extendom';

export class Form {
  static changed = false;
  constructor(protected readonly form: HTMLFormElement) {
  }
}

export class PostForm extends Form {
  constructor(form: HTMLFormElement) {
    super(form);
    for (const type of ['focusin', 'pointerover'])
      form.addEventListener(type, event => {
        const element = event.target as Element;
        if (!Array.from(form.elements).includes(element))
          return;
        const control = element as form.Control;
        try {
          if (control.checkValidity())
            control.reportValidity();
        } catch (e) {
          console.log(control);
          throw e;
        }
      });
  }
  async valid(callback?: () => void) {
    const controls = form.Form.getControls(this.form);
    await form.Form.loading(async () => {
      for (const control of controls) {
        control.classList.remove('invalid-input');
        control.setCustomValidity('');
        if (!control.checkValidity())
          control.classList.add('invalid-input');
      }
      await callback?.();
    });
    const invalid = controls.find(
      control => control.classList.contains('invalid-input'));
    if (!invalid)
      return true;
    invalid.scrollIntoView({block: 'center'});
    invalid.focus();
    invalid.reportValidity();
    return false;
  }
  async submit(callback: (body: FormData) => void) {
    const body = new FormData;
    const method = this.form['_method'];
    if (method)
      body.append('_method', method.value);
    body.append('authenticity_token', this.form['authenticity_token'].value);
    callback(body);
    const response = await form.Form.loading(() => {
      return fetch(this.form.action, {method: this.form.method, body});
    });
    const html = await response.text();
    const range = document.createRange();
    range.selectNode(document.querySelector('#root')!);
    range.deleteContents();
    range.insertNode(range.createContextualFragment(html));
  }
}

export abstract class PostFormPage {
  abstract readonly xd: Extendom;
  mode: 'input' | 'preview' = 'input';
  private form!: PostForm;
  constructor() {
    window.addEventListener('beforeunload', event => {
      if (Form.changed)
        event.preventDefault();
    });
  }
  initiateForm(element: HTMLFormElement) {
    this.form = new PostForm(element);
  }
  formListeners() {
    return {
      onchange: this.onChangeForm,
      onpointerover: this.onPointerOverForm,
      onfocusin: this.onFocusInForm as any,
    };
  }
  private onChangeForm() {
    Form.changed = true;
  }
  private onPointerOverForm(event: Event) {
    const control = event.target as form.Control;
    if (control.classList.contains('invalid-input'))
      control.reportValidity();
  }
  private onFocusInForm(event: Event) {
    const control = event.target as form.Control;
    if (control.classList.contains('invalid-input'))
      control.reportValidity();
  }
  async onClickPreview(_event: Event, {data}: {data: unknown}) {
    if (!await this.valid())
      return;
    this.mode = 'preview';
    return data;
  }
  private valid() {
    return this.form.valid(this.validate);
  }
  async onClickBack() {
    this.mode = 'input';
    await this.xd.render();
  }
  async onClickSubmit() {
    await this.form.submit(this.appendToFormData);
    Form.changed = false;
  }
  protected abstract validate: () => void;
  protected abstract appendToFormData: (body: FormData) => void;
}

export class Uploader {
  readonly attachments: Attachment[] = [];
  constructor(private readonly max: number) {
  }
  export() {
    return this.attachments.map(attachment => attachment.export());
  }
  import(plain: ReturnType<typeof this.export>) {
    for (const object of plain) {
      const attachment = new Attachment(object.filename, object.href, {
        id: object.id, signedId: object.signedId,
      });
      this.attachments.push(attachment);
    }
  }
  get count() {
    return this.attachments.filter(attachment => attachment.alive).length;
  }
  async onChangeFile(_event: Event, {data, element}: {
    data: PostFormPage, element: HTMLInputElement}) {
    const files = element.files!;
    if (files.length + this.count > 5) {
      await dialog.alert(i18n('file_count_error'));
      return;
    }
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        await dialog.alert(i18n('file_size_error', {size: `${this.max}MB`}));
        continue;
      }
      this.attachments.push(
        new Attachment(file.name, URL.createObjectURL(file), {file}));
      data.xd.renderViewTransition(this);
    }
  }
  asJSON() {
    return {
      files: this.attachments
        .filter(attachment => attachment.alive && attachment.signedId)
        .map(attachment => attachment.signedId),
      filesAttachmentsAttributes: this.attachments
        .filter(attachment => !attachment.alive && attachment.id)
        .map(attachment => attachment.destroyedId()),
    }
  }
  appendToFormData(body: FormData, name: string) {
    for (const attachment of this.attachments)
      if (attachment.alive && attachment.file)
        body.append(name, attachment.file);
  }
}

class Attachment {
  alive = true;
  readonly id;
  readonly signedId;
  readonly file;
  constructor(readonly filename: string, readonly href: string, {
    id, signedId, file,
  }: {
    id?: number;
    signedId?: string;
    file?: File;
  }) {
    this.id = id ?? 0;
    this.signedId = signedId ?? '';
    this.file = file;
  }
  export() {
    return {
      id: this.id,
      signedId: this.signedId,
      href: this.href,
      filename: this.filename,
    };
  }
  onClickView(event: Event) {
    event.preventDefault();
    window.open(this.href);
  }
  onClickDelete(_event: Event, {data, scope}: {
    data: PostFormPage, scope: Uploader
  }) {
    this.alive = false;
    Form.changed = true;
    data.xd.renderViewTransition(scope);
  }
  destroyedId() {
    return {
      id: this.id,
      _destroy: 1,
    };
  }
}
